/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, September 26th 2021, 11:03:49 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import * as React from "react";

import { stableSort, getComparator, buildAPIOrderingParams, buildAPISearchParams, filterObjectArray } from "lib/utils";
import { useAPIFetchPaginatedList } from "lib/api";
import { APIServicePathType, ObjectValue, SortDirection, ObjectKey } from "lib/types";
import { intersection, union } from "lodash";
import { DataTableProps } from "ui-widgets/lists/datatable/DataTable";
/**
 *
 * @param dataSource The data source that can either be an array or a OneStop
 * API Endpoint paginated via Django REST: https://www.django-rest-framework.org/api-guide/pagination/
 * @param uniqueKey A keyof the data object that is unique in the provided list of items e.g. id
 * @param initialRowsPerPage The default number of rows to show per page for pagination
 * @param defaultOrderBy The default key of the data object to order the list of items by
 * @returns destructed items consisiting of state variables and functions that are used by the data table.
 *
 * The is hook builds the state and event handlers for a DataTable Component that can render
 * both data from an array or a provided paginated API resource.
 * A paginated API resource is one that can take limit and offset parameters to browse through data.
 * For API resource that is not paginated, the parent component can just fetch the items and pass the resulting
 * array as the data source for this Data Table
 *
 */

export const useBuildDataTable = <OSObjectType, CustomKeys extends string = null>({
  dataSource,
  uniqueKey,
  initialRowsPerPage = 10,
  defaultOrderBy,
  apiFilterParams,
  ...props
}: DataTableProps<OSObjectType, CustomKeys>) => {
  const [order, setOrder] = React.useState<SortDirection>("asc");

  const [orderBy, setOrderBy] = React.useState<ObjectKey<OSObjectType>>(defaultOrderBy);

  const [searchText, SetSearchText] = React.useState<string>("");

  const isPaginatedAPISource = !Array.isArray(dataSource);

  // Refresh the api List
  const [refreshAPIList, setRefreshAPIList] = React.useState<boolean>(false);

  const [apiLimit, setAPILimit] = React.useState<number>(initialRowsPerPage);

  const {
    items,
    isMakingAPIRequest,
    isAPIError,
    httpResponseStatusCode,
    apiTotalItems,
    apiPageOffset: apiOffset,
    setAPIPageOffset: setAPIOffset,
  } = useAPIFetchPaginatedList<OSObjectType>({
    apiServicePath: isPaginatedAPISource ? (dataSource as APIServicePathType) : undefined,
    apiQueryParams: {
      ...buildAPISearchParams({
        apiSearchText: searchText,
      }),
      ...buildAPIOrderingParams({ apiOrder: order, apiOrderBy: orderBy }),
      ...apiFilterParams,
    },
    dependencyList: [orderBy, order, searchText, refreshAPIList],
    apiPageLimit: apiLimit,
    makeRequestOnInitialMount: true,
  });

  const rows = isPaginatedAPISource ? items ?? [] : (dataSource as OSObjectType[]);

  const rowCount = isPaginatedAPISource && apiTotalItems ? apiTotalItems : rows.length;

  const currentAPIPage = apiOffset / apiLimit + 1;

  const muiPage = isPaginatedAPISource ? currentAPIPage - 1 : 0; // currentAPIPage -1 because of the zero based index of MUI Pagination

  const [rowsPerPage, setRowsPerPage] = React.useState(isPaginatedAPISource ? apiLimit : initialRowsPerPage);

  // For unpaginated data sources.
  const [filteredState, setFilteredState] = React.useState<{
    isFiltered: boolean;
    filteredRows: ObjectValue<OSObjectType>[];
  }>({ isFiltered: false, filteredRows: [] });

  const [selectedRowsKeys, setSelectedRowsKeys] = React.useState<readonly ObjectValue<OSObjectType>[]>([]);

  const [page, setPage] = React.useState(muiPage);

  // Setting the page when offset for an api source changes
  React.useEffect(() => {
    if (isPaginatedAPISource) {
      setPage(muiPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiOffset]);

  // Setting the page when rows for a non-api source changes
  React.useEffect(() => {
    if (!isPaginatedAPISource) {
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rows)]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: ObjectKey<OSObjectType>) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    isPaginatedAPISource && setAPIOffset(apiLimit * newPage); // newPage already includes -1 to consider MUI zero-based index
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const new_rows_per_page = parseInt(event.target.value, 10);
    isPaginatedAPISource && setAPILimit(new_rows_per_page);
    setRowsPerPage(new_rows_per_page);
    setPage(0);
  };

  const handleSearch = React.useCallback(
    (search_text: string) => {
      if (isPaginatedAPISource) {
        // Note, for a better use experience, this function is only called when user has paused/stopped typing, resulting in an API request
        filterPaginatedSource(search_text);
      } else {
        filterUnpaginatedSource(search_text);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(rows)]
  );

  const filterPaginatedSource = (search_text: string) => SetSearchText(search_text ?? "");

  const filterUnpaginatedSource = (search_text: string) => {
    const filtered = filterObjectArray({
      objectArray: rows,
      searchKeys: [props.titleKey, ...props.searchKeys],
      searchText: search_text,
    });

    setFilteredState({
      isFiltered: Boolean(search_text),
      filteredRows: filtered.map((v) => v[uniqueKey]),
    });
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelectedRowsKeys = rows.map((n) => n[uniqueKey]);
      setSelectedRowsKeys(union(selectedRowsKeys, newSelectedRowsKeys)); // using lodash union to combine the previously selected bunch with the newly selected
      return;
    }
    setSelectedRowsKeys([]);
  };

  const handleSelectRow = (event: React.MouseEvent<unknown>, rowIdentifier: ObjectValue<OSObjectType>) => {
    const selectedIndex = selectedRowsKeys.indexOf(rowIdentifier);
    let newSelectedRowsKeys: readonly ObjectValue<OSObjectType>[] = [];

    if (selectedIndex === -1) {
      newSelectedRowsKeys = newSelectedRowsKeys.concat(selectedRowsKeys, rowIdentifier);
    } else if (selectedIndex === 0) {
      newSelectedRowsKeys = newSelectedRowsKeys.concat(selectedRowsKeys.slice(1));
    } else if (selectedIndex === selectedRowsKeys.length - 1) {
      newSelectedRowsKeys = newSelectedRowsKeys.concat(selectedRowsKeys.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedRowsKeys = newSelectedRowsKeys.concat(
        selectedRowsKeys.slice(0, selectedIndex),
        selectedRowsKeys.slice(selectedIndex + 1)
      );
    }

    setSelectedRowsKeys(newSelectedRowsKeys);
  };

  const rowIsSelected = (rowIdentifier: ObjectValue<OSObjectType>) => selectedRowsKeys.indexOf(rowIdentifier) !== -1;

  const allRowsSelected = () => {
    if (isPaginatedAPISource) {
      // using lodash intersection to check if all the rows in the current page are present in the
      // selectedRows object.
      return (
        rows.length > 0 &&
        rows.length ===
          intersection(
            rows.map((v) => v[uniqueKey]),
            selectedRowsKeys
          ).length
      );
    } else {
      return rowCount > 0 && selectedRowsKeys.length === rowCount;
    }
  };

  const handleRefreshList = () => {
    setRefreshAPIList(!refreshAPIList);
  };

  const formatData = () => {
    // Only slice rows if the full list has been provided. Otherwise, show all the data for the current page as
    // provided by paginated API results.

    /* if you don't need to support IE11, you can replace the `stableSort` call with:
    rows.slice().sort(getComparator(order, orderBy)) */

    if (isPaginatedAPISource) {
      return rows;
    } else {
      const filtered_data = filteredState.isFiltered
        ? rows.filter((v) => filteredState.filteredRows.includes(v[uniqueKey]))
        : rows;

      return stableSort<OSObjectType>(filtered_data, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
    }
  };

  return {
    allLoadedData: rows,
    rowsToDisplay: formatData(),
    page,
    rowsPerPage,
    rowCount,
    selectedRowsKeys,
    showLoader: isPaginatedAPISource ? isMakingAPIRequest : false,
    showError: isPaginatedAPISource ? isAPIError : false,
    httpResponseStatusCode,
    order,
    orderBy,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,
    extraSearchProps: { handleSearch, debounceSearch: isPaginatedAPISource },
    isFiltered: searchText || filteredState.isFiltered,
    searchText,
    handleSelectAllClick,
    handleSelectRow,
    allRowsSelected,
    rowIsSelected,
    handleRefreshList: isPaginatedAPISource && handleRefreshList,
  };
};
